import { Box, Skeleton, Tab, Tabs, ToggleButtonGroup, Typography } from "@mui/material";
import type { DayOfWeek } from "@prisma/client";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { StyledToggleButton } from "@/booking/Styles";
import type { BookingArea } from "@/server-fns/booking/bookingAreas";
import type { BookingTime, BookingTimesByDateResult } from "@/server-fns/booking/query";
import { getTimesByDate } from "@/server-fns/booking/serverFns";
import type { DateYYYYMMDD, TimeHHMM } from "@/types/date";

// What picking a time in a seating area sets on the guest form, handed over as one value so the
// form can apply it in a single update (see Form.tsx). An empty time clears the pick.
export type AreaSelection = {
    time: string;
    areaId: number | null | undefined;
    areaName: string | undefined;
};

type Props = {
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    storeId: string;
    bookingDate: DateYYYYMMDD;
    dayOfWeek: DayOfWeek;
    totalGuests: number;
    initialTime?: TimeHHMM;
    onValidationComplete?: (isValid: boolean) => void;
    allowInitialTimeRegardlessOfCapacity?: boolean;
    excludeBookingId?: string;
    bypassLeadTime?: boolean;
    /** Admin-only "Dining duration" override — when set, slot overlap is computed against this
     * duration instead of the store's default. Unset by the guest-facing form. */
    durationMinutesOverride?: number;
    /** Guest-facing: when given and the store has two or more seating areas, the times are listed
     * per area and picks are reported here instead of through `setFieldValue`. Omitted by the
     * admin booking form, which keeps the plain list. */
    onSelectInArea?: (selection: AreaSelection) => void;
};

export const getAvailableTimes = (times: BookingTime[] | undefined) =>
    times?.filter((time) => time.hasCapacity) ?? [];

export const getNoAvailableTimesMessage = ({
    totalGuests,
    minimumGuestCount,
}: {
    totalGuests: number;
    minimumGuestCount: number | null | undefined;
}) => {
    if (minimumGuestCount && totalGuests < minimumGuestCount) {
        return `Bookings for this venue require at least ${minimumGuestCount} guests. Please increase party size to see available times.`;
    }
    return "Sorry, there are no available times for booking.";
};

export const AvailableTimes = ({
    setFieldValue,
    storeId,
    bookingDate,
    dayOfWeek,
    totalGuests,
    initialTime,
    onValidationComplete,
    allowInitialTimeRegardlessOfCapacity = false,
    excludeBookingId,
    bypassLeadTime,
    durationMinutesOverride,
    onSelectInArea,
}: Props) => {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: [
            "bookingTimes",
            storeId,
            bookingDate,
            dayOfWeek,
            totalGuests,
            excludeBookingId,
            bypassLeadTime,
            durationMinutesOverride,
        ],
        queryFn: () =>
            getTimesByDate({
                data: {
                    storeId,
                    date: bookingDate,
                    dayOfWeek,
                    totalGuests,
                    excludeBookingId,
                    bypassLeadTime,
                    durationMinutesOverride,
                },
            }),
        placeholderData: keepPreviousData,
    });
    const [selectedTime, setSelectedTime] = useState<BookingTime | null>(
        initialTime
            ? { bookingStartTimeHHMM: initialTime, hasCapacity: true, availableAreaIds: [] }
            : null,
    );
    // undefined = no area picked (also the only value in the plain list)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null | undefined>(undefined);
    // The area tab the guest opened; undefined = still on the default tab.
    const [tabAreaId, setTabAreaId] = useState<number | null | undefined>(undefined);
    const prevBookingDateRef = useRef<string | null>(null);
    const prevDurationRef = useRef<number | undefined>(undefined);
    const bookingTimes = data?.bookingTimes;
    const areas = data?.areas ?? [];
    const selectInArea = areas.length >= 2 ? onSelectInArea : undefined;
    const isSectioned = selectInArea !== undefined;

    useEffect(() => {
        if (bookingTimes) {
            // With areas a time only counts once an area is picked and still has room in it.
            const isOpen = (t: BookingTime) =>
                isSectioned
                    ? selectedAreaId !== undefined && t.availableAreaIds.includes(selectedAreaId)
                    : t.hasCapacity;
            const isValid = initialTime
                ? allowInitialTimeRegardlessOfCapacity ||
                  !!bookingTimes.find((t) => t.bookingStartTimeHHMM === initialTime && isOpen(t))
                : false;
            onValidationComplete?.(isValid);
            if (!isValid && initialTime) {
                setSelectedTime(null);
                if (selectInArea) {
                    setSelectedAreaId(undefined);
                    selectInArea({ time: "", areaId: undefined, areaName: undefined });
                } else {
                    setFieldValue("time", "");
                }
            }
        }
    }, [
        bookingTimes,
        initialTime,
        isSectioned,
        selectInArea,
        selectedAreaId,
        allowInitialTimeRegardlessOfCapacity,
        onValidationComplete,
        setFieldValue,
    ]);

    useEffect(() => {
        if (!initialTime) {
            setSelectedTime(null);
            setSelectedAreaId(undefined);
        }
    }, [initialTime]);

    useEffect(() => {
        if (prevBookingDateRef.current !== null && prevBookingDateRef.current !== bookingDate) {
            setSelectedTime(null);
            setSelectedAreaId(undefined);
            setTabAreaId(undefined);
        }
        prevBookingDateRef.current = bookingDate;
    }, [bookingDate]);

    // Changing the duration override changes which slots actually have capacity (a previously
    // valid selection may no longer fit) — clear the selection the same way a date change does,
    // rather than leaving a now-possibly-invalid time looking selected.
    useEffect(() => {
        if (
            prevDurationRef.current !== undefined &&
            prevDurationRef.current !== durationMinutesOverride
        ) {
            setSelectedTime(null);
            setSelectedAreaId(undefined);
        }
        prevDurationRef.current = durationMinutesOverride;
    }, [durationMinutesOverride]);

    const renderLoading = () =>
        [
            "12:00",
            "12:10",
            "12:20",
            "12:30",
            "12:40",
            "12:50",
            "13:00",
            "13:10",
            "13:20",
            "13:30",
            "13:40",
        ].map((i) => (
            <Skeleton key={i} sx={{ maxWidth: "none" }}>
                <StyledToggleButton fullWidth key={i} value={i}>
                    {i}
                </StyledToggleButton>
            </Skeleton>
        ));

    const renderEmptyView = (result: BookingTimesByDateResult | undefined) => (
        <Typography variant="h6" color="error">
            {getNoAvailableTimesMessage({
                totalGuests,
                minimumGuestCount: result?.minimumGuestCount,
            })}
        </Typography>
    );

    if (isLoading) {
        return (
            <ToggleButtonGroup
                fullWidth
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0px, 1fr))",
                    columnGap: 2,
                }}
            >
                {renderLoading()}
            </ToggleButtonGroup>
        );
    }

    if (bookingTimes && bookingTimes.length > 0) {
        const handleSelect = (time: BookingTime, area?: BookingArea) => {
            setSelectedTime(time);
            if (area && selectInArea) {
                setSelectedAreaId(area.id);
                selectInArea({
                    time: time.bookingStartTimeHHMM,
                    areaId: area.id,
                    areaName: area.name,
                });
            } else {
                setFieldValue("time", time.bookingStartTimeHHMM);
            }
        };

        const fetchingSx = {
            opacity: isFetching ? 0.4 : 1,
            transition: "opacity 0.2s ease",
            pointerEvents: isFetching ? "none" : "auto",
        } as const;
        const gridSx = {
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0px, 1fr))",
            columnGap: 2,
        } as const;

        // `area` is set for a section of the per-area layout: availability and the highlighted
        // button are then that area's, instead of the store-wide ones.
        const renderTimeGrid = (area?: BookingArea) => (
            <ToggleButtonGroup
                exclusive
                onChange={(_, newValue) => {
                    if (!newValue) return;
                    handleSelect(newValue, area);
                }}
                fullWidth
                sx={area ? gridSx : { ...gridSx, ...fetchingSx }}
            >
                {bookingTimes.map((time) => (
                    <StyledToggleButton
                        fullWidth
                        key={time.bookingStartTimeHHMM}
                        value={time}
                        selected={
                            selectedTime?.bookingStartTimeHHMM === time.bookingStartTimeHHMM &&
                            (!area || selectedAreaId === area.id)
                        }
                        disabled={
                            area ? !time.availableAreaIds.includes(area.id) : !time.hasCapacity
                        }
                    >
                        {time.bookingStartTimeHHMM}
                    </StyledToggleButton>
                ))}
            </ToggleButtonGroup>
        );

        if (!isSectioned) return renderTimeGrid();

        const areaKey = (area: BookingArea) => String(area.id ?? "general");
        const openCount = (area: BookingArea) =>
            bookingTimes.filter((time) => time.availableAreaIds.includes(area.id)).length;
        // One area's times at a time keeps the form short however many areas there are. Until
        // the guest picks a tab, start on the first area that has anything open.
        const activeArea =
            areas.find((area) => area.id === tabAreaId) ??
            areas.find((area) => openCount(area) > 0) ??
            areas[0];

        return (
            <Box sx={fetchingSx}>
                <Tabs
                    value={areaKey(activeArea)}
                    onChange={(_, key: string) =>
                        setTabAreaId(areas.find((area) => areaKey(area) === key)?.id)
                    }
                    variant={areas.length > 3 ? "scrollable" : "fullWidth"}
                    aria-label="Seating area"
                    sx={{ mb: 2 }}
                >
                    {areas.map((area) => (
                        <Tab
                            key={areaKey(area)}
                            id={`seating-area-tab-${areaKey(area)}`}
                            aria-controls="seating-area-panel"
                            value={areaKey(area)}
                            label={openCount(area) > 0 ? area.name : `${area.name} · No times`}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        />
                    ))}
                </Tabs>
                <Box
                    role="tabpanel"
                    id="seating-area-panel"
                    aria-labelledby={`seating-area-tab-${areaKey(activeArea)}`}
                >
                    {renderTimeGrid(activeArea)}
                </Box>
            </Box>
        );
    }

    return renderEmptyView(data);
};
